import { useEffect, useRef, useState } from 'react';

// Try to import framer-motion, but handle gracefully if not available
let motion, useInView;
try {
  const framerMotion = require('framer-motion');
  motion = framerMotion.motion;
  useInView = framerMotion.useInView;
} catch (e) {
  // Framer-motion not installed, use fallback
  motion = null;
  useInView = null;
}

// Component for fade-in animation on scroll
export default function ScrollAnimation({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    if (!ref.current) return;

    // Use Intersection Observer if framer-motion's useInView is not available
    if (!useInView) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.disconnect();
          }
        },
        { threshold: 0.1, rootMargin: '-100px' }
      );

      observer.observe(ref.current);
      return () => observer.disconnect();
    }
  }, []);

  // If framer-motion is available, use it
  if (motion && useInView) {
    const MotionDiv = motion.div;
    const FramerScrollAnimation = () => {
      const framerIsInView = useInView(ref, { once: true, margin: '-100px' });
      return (
        <MotionDiv
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={framerIsInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
          transition={{ duration: 0.6, delay }}
          className={className}
        >
          {children}
        </MotionDiv>
      );
    };
    return <FramerScrollAnimation />;
  }

  // Fallback: Use CSS transitions
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(50px)',
        transition: `opacity 0.6s ease-out ${delay}s, transform 0.6s ease-out ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}
